import { Link } from "react-router-dom";
import { ArrowRight, Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp } from "lucide-react";
import { SERVICES } from "@/lib/content";
import CTASection from "@/components/CTASection";
import useSEO from "@/lib/useSEO";

const ICONS = { Gavel, FileSignature, Copyright, Building2, ShieldCheck, TrendingUp };

export default function Services() {
  useSEO({
    title: "Creator Legal Services — YouTube, TikTok, Brand Deals, IP, Reputation | Upload Counsel",
    description: "Six legal surfaces for creators: platform disputes, contracts, IP and trademarks, business entity setup, reputation defense, and monetization.",
    canonical: "https://uploadlawyer.com/services",
  });
  return (
    <div data-testid="page-services">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-16 md:pt-32">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Services Hub</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight max-w-3xl">
            Six legal surfaces, all calibrated for the creator economy.
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Triage with Upload Counsel. Engagement happens directly with vetted attorneys in our network — matched to your jurisdiction and matter type.
          </p>
        </div>
      </section>

      <section className="relative pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Gavel;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`services-hub-card-${s.slug}`}
                className="beam-card group block rounded-3xl border border-[#272B36] bg-[#121419] p-8 md:p-10 hover:border-[#D9B36C]/50 transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                  <div className="flex items-start gap-5 lg:flex-1">
                    <div className="h-12 w-12 shrink-0 rounded-xl bg-gradient-to-br from-[#D9B36C]/20 to-[#7C8DB0]/20 border border-[#D9B36C]/30 flex items-center justify-center">
                      <Icon size={22} className="text-[#D9B36C]" />
                    </div>
                    <div>
                      <p className="font-mono text-xs text-slate-500 tracking-widest">0{i + 1}</p>
                      <h2 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">{s.title}</h2>
                      <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">{s.description}</p>
                    </div>
                  </div>
                  <div className="lg:w-72 lg:shrink-0">
                    <ul className="space-y-2">
                      {s.bullets.slice(0, 4).map((b) => (
                        <li key={b} className="text-sm text-slate-400 flex gap-2">
                          <span className="text-[#D9B36C] mt-1">›</span>
                          {b}
                        </li>
                      ))}
                    </ul>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#D9B36C] group-hover:gap-2 transition-all">
                      Deep dive <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <CTASection
        title="Not sure which surface your problem lives on?"
        body="Most creator matters touch two or three. Send one request; we sort out which apply and tell you the realistic path before you spend a dollar."
      />
    </div>
  );
}
